import { Modifier, ModifierActiveStatus } from '@/types/menus.types';

const PLAIN_INACTIVE = 'Inactive';
const INACTIVE_PRICE_PREFIX = 'Inactive-';
const PLAIN_NUMERIC_PRICE = /^\d+(\.\d+)?$/;

/** Exact category name returned by GET /modifiers/categories and PUT /modifiers. */
export const PIZZA_TOPPINGS_INGREDIENTS_CATEGORY = 'Pizza Toppings (Ingredients)';

/** Pizza categories where edit flow updates existing prices only (no activation on menus without a price). */
export const MODIFIER_CATEGORIES_PRICE_UPDATE_ONLY = new Set(['Pizza Cheese', 'Pizza Toppings (Pricing)']);

/** API / DB may return singular or plural for this category. */
export const APPETIZER_TOPPING_CATEGORIES = new Set(['Appetizer Topping', 'Appetizer Toppings']);

/**
 * Categories that use slot-based modifier selection (multi-select listbox or single Dropdown)
 * instead of modifier text search. Includes Other and Appetizer Topping(s).
 */
export const MODIFIER_CATEGORIES_WITH_MODIFIER_MULTI_SELECT = new Set(['Other', ...APPETIZER_TOPPING_CATEGORIES]);

/**
 * Categories that show a single-select Modifier Dropdown (compiled A–Z across slots).
 * Other uses this for SPTK-419 (keystroke jump + one-row layout).
 */
export const MODIFIER_CATEGORIES_WITH_MODIFIER_SINGLE_SELECT = new Set(['Other']);

/**
 * Categories that also require a subcategory (slot) dropdown before modifier selection.
 * Empty after SPTK-419 (Other no longer shows Subcategory).
 */
export const MODIFIER_CATEGORIES_WITH_SUBCATEGORY_FILTER = new Set<string>();

/**
 * Categories that show only Category + Modifier text search (no Price / Option1 / Option2),
 * and use All/Some/None presence cells instead of prices.
 */
export const MODIFIER_CATEGORIES_MODIFIER_SEARCH_ONLY = new Set([PIZZA_TOPPINGS_INGREDIENTS_CATEGORY]);

/** @deprecated Use MODIFIER_CATEGORIES_WITH_MODIFIER_MULTI_SELECT */
export const MODIFIER_CATEGORIES_WITH_SLOT_FILTERS = MODIFIER_CATEGORIES_WITH_MODIFIER_MULTI_SELECT;

export function isModifierCategoryPriceUpdateOnly(categoryName: string): boolean {
  return MODIFIER_CATEGORIES_PRICE_UPDATE_ONLY.has(categoryName);
}

export function isModifierCategoryAppetizerTopping(categoryName: string): boolean {
  return APPETIZER_TOPPING_CATEGORIES.has(categoryName);
}

export function isModifierCategoryWithModifierMultiSelect(categoryName: string): boolean {
  return MODIFIER_CATEGORIES_WITH_MODIFIER_MULTI_SELECT.has(categoryName);
}

export function isModifierCategoryWithModifierSingleSelect(categoryName: string): boolean {
  return MODIFIER_CATEGORIES_WITH_MODIFIER_SINGLE_SELECT.has(categoryName);
}

export function isModifierCategoryWithSubcategoryFilter(categoryName: string): boolean {
  return MODIFIER_CATEGORIES_WITH_SUBCATEGORY_FILTER.has(categoryName);
}

export function isModifierCategoryWithSlotFilters(categoryName: string): boolean {
  return isModifierCategoryWithModifierMultiSelect(categoryName);
}

export function isModifierCategoryIngredientsPresence(categoryName: string): boolean {
  return MODIFIER_CATEGORIES_MODIFIER_SEARCH_ONLY.has(categoryName);
}

export function isModifierCategoryModifierSearchOnly(categoryName: string): boolean {
  return isModifierCategoryIngredientsPresence(categoryName);
}

export type ModifierSlotFilterOption = {
  modifierId: string;
  modifierName: string;
};

export type ModifierSlotFilterSlot = {
  value: string;
  label: string;
  modifiers: ModifierSlotFilterOption[];
};

/** Case-insensitive dedup key for modifier display names (Other / Appetizer dropdown + search expansion). */
export function normalizeModifierNameKey(name: string): string {
  return name
    .normalize('NFKC')
    .replace(/\u00a0/g, ' ')
    .replace(/[\u2010-\u2015\u2212-]/g, '')
    .replace(/\s+/g, '')
    .trim()
    .toLocaleLowerCase();
}

/**
 * Flatten slot modifiers (one entry per modifier id), sorted alphabetically.
 * Duplicate display names across slots (e.g. Bacon) remain as separate rows.
 * Prefer flattenUniqueModifierNamesFromSlots for Other / Appetizer filter dropdowns.
 */
export function flattenModifiersFromSlots(slots: ModifierSlotFilterSlot[]): ModifierSlotFilterOption[] {
  const byId = new Map<string, ModifierSlotFilterOption>();
  for (const slot of slots) {
    for (const modifier of slot.modifiers) {
      byId.set(modifier.modifierId, modifier);
    }
  }
  return [...byId.values()].sort((a, b) =>
    a.modifierName.localeCompare(b.modifierName, undefined, { sensitivity: 'base' }),
  );
}

/**
 * Flatten slot modifiers (one entry per unique name, case-insensitive), sorted alphabetically.
 * Same display name across slots (e.g. Bacon) appears once; the first id is kept as the value.
 * Used for Other single-select and Appetizer Topping(s) multi-select filter lists.
 */
export function flattenUniqueModifierNamesFromSlots(slots: ModifierSlotFilterSlot[]): ModifierSlotFilterOption[] {
  const byName = new Map<string, ModifierSlotFilterOption>();
  for (const slot of slots) {
    for (const modifier of slot.modifiers) {
      const key = normalizeModifierNameKey(modifier.modifierName);
      if (!byName.has(key)) {
        byName.set(key, modifier);
      }
    }
  }
  return [...byName.values()].sort((a, b) =>
    a.modifierName.localeCompare(b.modifierName, undefined, { sensitivity: 'base' }),
  );
}

/**
 * Expand selected modifier id(s) to every id that shares the same display name
 * across the relevant slots (all slots, or one subcategory slot when provided).
 * Used so Other / Appetizer search returns every slot row for a chosen name (e.g. Bacon).
 */
export function expandModifierIdsBySharedName(
  slots: ModifierSlotFilterSlot[],
  selectedIds: string[],
  subCategorySlotId?: string,
): string[] {
  if (!selectedIds.length || !slots.length) return [];

  const scopedSlots = subCategorySlotId ? slots.filter((slot) => slot.value === subCategorySlotId) : slots;
  const allModifiers = scopedSlots.flatMap((slot) => slot.modifiers);
  if (!allModifiers.length) return [];

  const selectedIdSet = new Set(selectedIds);
  const selectedNameKeys = new Set<string>();
  for (const modifier of allModifiers) {
    if (selectedIdSet.has(modifier.modifierId)) {
      selectedNameKeys.add(normalizeModifierNameKey(modifier.modifierName));
    }
  }
  if (!selectedNameKeys.size) return [...selectedIds];

  const expandedIds: string[] = [];
  const seen = new Set<string>();
  for (const modifier of allModifiers) {
    if (!selectedNameKeys.has(normalizeModifierNameKey(modifier.modifierName))) continue;
    if (seen.has(modifier.modifierId)) continue;
    seen.add(modifier.modifierId);
    expandedIds.push(modifier.modifierId);
  }
  return expandedIds;
}

/** Default selection: first modifier in flattened alphabetical order. */
export function getDefaultModifierIdsFromSlots(slots: ModifierSlotFilterSlot[]): string[] {
  const flat = flattenModifiersFromSlots(slots);
  return flat.length ? [flat[0].modifierId] : [];
}

/** Default subcategory (first slot, alphabetical by slot label). */
export function getDefaultSubcategorySlotId(slots: ModifierSlotFilterSlot[]): string {
  return slots[0]?.value ?? '';
}

/**
 * Default modifier id(s) for slot-based categories.
 * When subCategorySlotId is set, uses only that slot's modifiers; otherwise flattens all slots.
 */
export function getDefaultModifierIdsForCategory(
  slots: ModifierSlotFilterSlot[],
  subCategorySlotId?: string,
): string[] {
  if (subCategorySlotId) {
    const slot = slots.find((s) => s.value === subCategorySlotId);
    return slot ? getDefaultModifierIdsFromSlots([slot]) : [];
  }
  return getDefaultModifierIdsFromSlots(slots);
}

export function resolveSlotIdForModifierFetch(
  categoryName: string,
  slots: ModifierSlotFilterSlot[],
  subCategory: string | undefined,
  modifierIds: string[],
): string {
  if (isModifierCategoryWithSubcategoryFilter(categoryName)) {
    return subCategory ?? '';
  }
  if (!modifierIds.length) {
    return slots[0]?.value ?? '';
  }
  const slotForFirstSelected = slots.find((slot) =>
    slot.modifiers.some((modifier) => modifier.modifierId === modifierIds[0]),
  );
  if (slotForFirstSelected) {
    return slotForFirstSelected.value;
  }
  const slotForAnySelected = slots.find((slot) =>
    slot.modifiers.some((modifier) => modifierIds.includes(modifier.modifierId)),
  );
  return slotForAnySelected?.value ?? slots[0]?.value ?? '';
}

export function isZeroPricedModifierSelection(modifiers: Modifier[]): boolean {
  return modifiers.length > 0 && modifiers.every((modifier) => modifier.isZeroPriced === true);
}

export function normalizeChosenPriceRange(chosenPriceRange: string | undefined): string {
  if (chosenPriceRange == null) return '';
  return String(chosenPriceRange).trim();
}

/**
 * Fully inactive for visibility/hidden-row rules: empty or exact "Inactive" only.
 * Values like "Inactive-2.00" still count as having listing context.
 */
export function isChosenPriceFullyInactiveForVisibility(chosenPriceRange: string | undefined): boolean {
  const normalized = normalizeChosenPriceRange(chosenPriceRange);
  return normalized === '' || normalized === PLAIN_INACTIVE;
}

export function hasActiveChosenPrice(chosenPriceRange: string | undefined): boolean {
  return !isChosenPriceFullyInactiveForVisibility(chosenPriceRange);
}

/** Products-style edit block: "Inactive-" prefix or plain numeric price (e.g. "10.69"). */
export function hasRelevantActivePriceInStoreGroup(chosenPriceRange: string | undefined): boolean {
  const normalized = normalizeChosenPriceRange(chosenPriceRange);
  if (!normalized) return false;
  return normalized.startsWith(INACTIVE_PRICE_PREFIX) || PLAIN_NUMERIC_PRICE.test(normalized);
}

/** True when the store group has any existing price context (active, inactive-with-price, or range). */
export function hasExistingPriceInStoreGroup(chosenPriceRange: string | undefined): boolean {
  const normalized = normalizeChosenPriceRange(chosenPriceRange);
  return normalized !== '' && normalized !== PLAIN_INACTIVE;
}

export function modifierHasExistingPriceInStoreGroup(
  modifier: Pick<Modifier, 'storeGroups'>,
  storeGroupName: string,
): boolean {
  return hasExistingPriceInStoreGroup(getChosenPriceForStoreGroup(modifier, storeGroupName));
}

export function getStoreGroupNamesWithExistingPrices(modifiers: Modifier[], storeGroupNames: string[]): string[] {
  return storeGroupNames.filter((storeGroupName) =>
    modifiers.some((modifier) => modifierHasExistingPriceInStoreGroup(modifier, storeGroupName)),
  );
}

/**
 * For price-update-only categories: stores in the user's selection that already have a price
 * for this modifier (from store groups with existing chosenPriceRange).
 */
export function getStoresWithExistingPricesForModifier(
  modifier: Pick<Modifier, 'storeGroups'>,
  selectedStoreGroupNames: string[],
  selectedStoreNumbers: string[],
): string[] {
  if (!selectedStoreGroupNames.length || !selectedStoreNumbers.length) {
    return [];
  }

  const selectedStoreSet = new Set(selectedStoreNumbers);
  const selectedGroupSet = new Set(selectedStoreGroupNames);
  const storesWithPrice = new Set<string>();

  for (const storeGroup of modifier.storeGroups) {
    if (!selectedGroupSet.has(storeGroup.storeGroupName)) {
      continue;
    }
    if (!hasExistingPriceInStoreGroup(storeGroup.chosenPriceRange)) {
      continue;
    }

    for (const storeNo of storeGroup.stores ?? []) {
      if (selectedStoreSet.has(storeNo)) {
        storesWithPrice.add(storeNo);
      }
    }
  }

  return Array.from(storesWithPrice);
}

/**
 * Selected stores that should be sent on deactivate:
 * - priced modifiers: store groups with an existing price (including Inactive-$X / mixed)
 * - ingredients: store groups with ALL or SOME presence
 * Fully inactive / NONE groups are excluded. Mixed groups still include all their selected stores.
 */
export function getStoresEligibleForModifierDeactivation(
  modifiers: Pick<Modifier, 'storeGroups'>[],
  selectedStoreGroupNames: string[],
  selectedStoreNumbers: string[],
  options?: { useActiveStatus?: boolean },
): string[] {
  if (!selectedStoreGroupNames.length || !selectedStoreNumbers.length || !modifiers.length) {
    return [];
  }

  const selectedStoreSet = new Set(selectedStoreNumbers);
  const selectedGroupSet = new Set(selectedStoreGroupNames);
  const eligibleStores = new Set<string>();
  const useActiveStatus = options?.useActiveStatus === true;

  modifiers.forEach((modifier) => {
    modifier.storeGroups?.forEach((storeGroup) => {
      if (!selectedGroupSet.has(storeGroup.storeGroupName)) {
        return;
      }

      const isEligible = useActiveStatus
        ? storeGroup.activeStatus === 'ALL' || storeGroup.activeStatus === 'SOME'
        : hasExistingPriceInStoreGroup(storeGroup.chosenPriceRange);

      if (!isEligible) {
        return;
      }

      (storeGroup.stores ?? []).forEach((storeNo) => {
        if (selectedStoreSet.has(storeNo)) {
          eligibleStores.add(storeNo);
        }
      });
    });
  });

  return Array.from(eligibleStores);
}

function getChosenPriceForStoreGroup(
  modifier: Pick<Modifier, 'storeGroups'>,
  storeGroupName: string,
): string | undefined {
  return modifier.storeGroups.find((group) => group.storeGroupName === storeGroupName)?.chosenPriceRange;
}

function modifierHasStoreGroupMatching(
  modifier: Modifier,
  predicate: (chosenPriceRange: string | undefined) => boolean,
): boolean {
  return modifier.storeGroups.some((storeGroup) => predicate(storeGroup.chosenPriceRange));
}

/**
 * True if the modifier is active on the menu in at least one selected store group
 * (any value other than empty / plain "Inactive").
 */
export function modifierHasActiveListingInSelectedStores(
  modifier: Modifier,
  selectedStoreGroupNames: string[],
): boolean {
  if (!selectedStoreGroupNames.length) return false;
  return selectedStoreGroupNames.some((name) => hasActiveChosenPrice(getChosenPriceForStoreGroup(modifier, name)));
}

export function getModifierActiveStatusForStoreGroup(
  modifier: Pick<Modifier, 'storeGroups'>,
  storeGroupName: string,
): ModifierActiveStatus {
  const status = modifier.storeGroups.find((group) => group.storeGroupName === storeGroupName)?.activeStatus;
  if (status === 'ALL' || status === 'SOME' || status === 'NONE') {
    return status;
  }
  return 'NONE';
}

export type IngredientStoreAvailability = {
  storeNumber: string;
  isActive: boolean;
};

export type IngredientAvailabilityDeviationRow = {
  productName: string;
  productDescription: string | null;
  storeAvailabilities: IngredientStoreAvailability[];
};

/**
 * Expands BE ingredient status deviations (majority + minority stores only)
 * into a full per-store Active/Inactive grid for the selected stores.
 */
export function expandIngredientStatusDeviations(
  data: Array<{
    productName: string;
    productDescription?: string | null;
    majorityPricePoint: string | null;
    priceDeviations: Array<{ storeNumber: string; status?: string; price?: string }>;
  }>,
  selectedStores: string[],
): IngredientAvailabilityDeviationRow[] {
  const sortedStores = [...selectedStores].sort();

  return data.map((product) => {
    const majorityIsActive = (product.majorityPricePoint ?? 'Active').toLowerCase() === 'active';
    const deviationByStore = new Map(
      product.priceDeviations.map((d) => [d.storeNumber, (d.status ?? '').toLowerCase() === 'active']),
    );

    const storeAvailabilities: IngredientStoreAvailability[] = sortedStores.map((storeNumber) => {
      if (deviationByStore.has(storeNumber)) {
        return { storeNumber, isActive: deviationByStore.get(storeNumber)! };
      }
      return { storeNumber, isActive: majorityIsActive };
    });

    return {
      productName: product.productName,
      productDescription: product.productDescription ?? null,
      storeAvailabilities,
    };
  });
}

function modifierUsesActiveStatusPresence(modifier: Pick<Modifier, 'storeGroups'>): boolean {
  return modifier.storeGroups.some((group) => group.activeStatus != null);
}

/** Hide row when isVisible is false and fully inactive in every selected store. */
export function shouldHideInvisibleModifier(
  modifier: Pick<Modifier, 'isVisible' | 'storeGroups'>,
  selectedStoreGroupNames: string[],
  options?: { hideAllInvisible?: boolean },
): boolean {
  if (modifier.isVisible !== false) return false;
  // Pizza Toppings (Ingredients): invisible modifiers are never listed.
  if (options?.hideAllInvisible || modifierUsesActiveStatusPresence(modifier)) {
    return true;
  }
  if (!selectedStoreGroupNames.length) return true;
  return selectedStoreGroupNames.every((name) =>
    isChosenPriceFullyInactiveForVisibility(getChosenPriceForStoreGroup(modifier, name)),
  );
}

/** Inactive in all selected store groups (only empty, missing, or plain "Inactive"). */
export function isModifierCompletelyInactiveAcrossSelection(
  modifier: Modifier,
  selectedStoreGroupNames: string[],
): boolean {
  if (!modifier?.storeGroups?.length) return true;
  if (!selectedStoreGroupNames.length) return true;

  if (modifierUsesActiveStatusPresence(modifier)) {
    return selectedStoreGroupNames.every((name) => getModifierActiveStatusForStoreGroup(modifier, name) === 'NONE');
  }

  return selectedStoreGroupNames.every((name) => {
    const storeGroup = modifier.storeGroups.find((group) => group.storeGroupName === name);
    if (!storeGroup) return true;
    return isChosenPriceFullyInactiveForVisibility(storeGroup.chosenPriceRange);
  });
}

/** Active (ALL) in every selected store group — used to disable Activate for ingredients. */
export function isModifierCompletelyActiveAcrossSelection(
  modifier: Modifier,
  selectedStoreGroupNames: string[],
): boolean {
  if (!modifier?.storeGroups?.length) return false;
  if (!selectedStoreGroupNames.length) return false;

  return selectedStoreGroupNames.every((name) => getModifierActiveStatusForStoreGroup(modifier, name) === 'ALL');
}

function parsePriceNumbers(normalized: string): number[] {
  if (!normalized || normalized === PLAIN_INACTIVE) return [];
  if (normalized.startsWith(INACTIVE_PRICE_PREFIX)) {
    const value = parseFloat(normalized.slice(INACTIVE_PRICE_PREFIX.length).trim());
    return Number.isNaN(value) ? [0] : [value];
  }
  return normalized
    .split(/-/)
    .map((part) => parseFloat(part.trim()))
    .filter((value) => !Number.isNaN(value));
}

/** First numeric value in a chosenPriceRange, handling Inactive-X and min-max ranges. */
export function getSortValueFromModifierChosenPrice(chosenPriceRange: string | undefined): number {
  const numbers = parsePriceNumbers(normalizeChosenPriceRange(chosenPriceRange));
  return numbers.length > 0 ? numbers[0]! : Number.NaN;
}

/** Sort key for a modifier column, using min or max of a range based on sort direction. */
export function getModifierColumnSortValue(item: Modifier, columnId: string, currentSort: 'asc' | 'desc'): number {
  const raw = item.storeGroups.find((group) => group.storeGroupName === columnId)?.chosenPriceRange;
  const numbers = parsePriceNumbers(normalizeChosenPriceRange(raw));
  if (numbers.length === 0) return Number.NaN;
  if (numbers.length === 1) return numbers[0]!;
  return currentSort === 'desc' ? numbers[numbers.length - 1]! : numbers[0]!;
}

export function formatModifierPriceCell(
  chosenPriceRange: string | undefined,
  inactiveLabel: string,
): { text: string; showRangeInfo: boolean } {
  const raw = normalizeChosenPriceRange(chosenPriceRange);
  if (!raw || raw === PLAIN_INACTIVE) {
    return { text: inactiveLabel, showRangeInfo: false };
  }

  if (raw.startsWith(INACTIVE_PRICE_PREFIX)) {
    const price = raw.slice(INACTIVE_PRICE_PREFIX.length).trim();
    return { text: `${inactiveLabel} - $${price}`, showRangeInfo: true };
  }

  const formatted = `$${raw}`.replace(/-/g, ' - ');
  const hasRange = formatted.includes(' - ') || (raw.includes('-') && !raw.startsWith(INACTIVE_PRICE_PREFIX));
  return { text: formatted, showRangeInfo: hasRange };
}

export function modifierHasPriceLockRelevantState(modifier: Modifier): boolean {
  return Boolean(modifier.priceIsLocked) && modifierHasStoreGroupMatching(modifier, hasRelevantActivePriceInStoreGroup);
}

/** Dollar amount, range, or Inactive-$X. A plain Inactive cell is not an active price. */
export function modifierHasActivePrice(modifier: Pick<Modifier, 'storeGroups'>): boolean {
  return (modifier.storeGroups ?? []).some((group) => hasActiveChosenPrice(group.chosenPriceRange));
}

export function modifierPriceLockWithActivePrice(modifier: Modifier): boolean {
  return Boolean(modifier.priceIsLocked) && modifierHasActivePrice(modifier);
}

export function modifierZeroLockWithActivePrice(modifier: Modifier): boolean {
  return modifier.isZeroPriced === true && modifierHasActivePrice(modifier);
}

/** Price lock or zero-priced lock that already has an active price cannot open Activate / Edit. */
export function modifierLockWithActivePriceBlocksEdit(modifier: Modifier): boolean {
  return modifierPriceLockWithActivePrice(modifier) || modifierZeroLockWithActivePrice(modifier);
}

/** Invisible modifiers with an active price: edit/activate blocked; deactivate still allowed. */
export function modifierHasInvisibleActivePriceState(modifier: Modifier): boolean {
  return modifier.isVisible === false && modifierHasStoreGroupMatching(modifier, hasRelevantActivePriceInStoreGroup);
}

export function modifierBlocksEditActivation(modifier: Modifier): boolean {
  return modifierLockWithActivePriceBlocksEdit(modifier) || modifierHasInvisibleActivePriceState(modifier);
}

function parseTargetPriceForComparison(targetPrice: string): number | null {
  const sanitized = String(targetPrice ?? '').trim();
  if (!sanitized) return null;
  const value = parseFloat(sanitized);
  return Number.isNaN(value) ? null : value;
}

function pricesEqual(a: number, b: number): boolean {
  return Number(a.toFixed(2)) === Number(b.toFixed(2));
}

/** True when chosenPriceRange is a single effective price equal to the target (not a range or inactive). */
export function isChosenPriceEqualToTargetPrice(chosenPriceRange: string | undefined, targetPrice: number): boolean {
  const normalized = normalizeChosenPriceRange(chosenPriceRange);
  if (!normalized || normalized === PLAIN_INACTIVE) {
    return false;
  }

  const numbers = parsePriceNumbers(normalized);
  if (numbers.length === 0) return false;
  if (numbers.length === 1) {
    return pricesEqual(numbers[0]!, targetPrice);
  }

  return pricesEqual(numbers[0]!, targetPrice) && pricesEqual(numbers[numbers.length - 1]!, targetPrice);
}

/**
 * True when every compared store group on every modifier already has the target price
 * (no price change would result from submission).
 */
export function areAllModifierStoreGroupPricesEqualToTarget(
  modifiers: Modifier[],
  storeGroupNames: string[],
  targetPrice: string,
  options?: { priceUpdateOnly?: boolean },
): boolean {
  if (!modifiers.length || !storeGroupNames.length) {
    return false;
  }

  const target = parseTargetPriceForComparison(targetPrice);
  if (target === null) {
    return false;
  }

  return modifiers.every((modifier) =>
    storeGroupNames.every((storeGroupName) => {
      if (options?.priceUpdateOnly && !modifierHasExistingPriceInStoreGroup(modifier, storeGroupName)) {
        return true;
      }

      const chosenPriceRange = modifier.storeGroups?.find(
        (group) => group.storeGroupName === storeGroupName,
      )?.chosenPriceRange;

      return isChosenPriceEqualToTargetPrice(chosenPriceRange, target);
    }),
  );
}
